import { Bank, Branch, RecoveryDepartment, User, UserRole, Zone } from '../types';

/**
 * ONE place that answers "what belongs to what".
 *
 * Bank -> Zonal Office -> Branch -> User.
 *
 * Before this file each screen decided parentage for itself, and none of them
 * agreed. The zone dropdown showed every zone in the database whatever bank
 * was chosen; a branch created under Bank of Maharashtra was filed with
 * `bankId: 'BNK-01'` (State Bank of India) because that was the hard-coded
 * default; and a user could be created with a branch belonging to a bank they
 * do not work for. None of it was visible, because nothing checked.
 *
 * Every dropdown, every validation and every scope check now comes from here.
 */

/* ------------------------------------------------------------------ *
 * Name matching
 *
 * Names arrive from three places -- typed by an administrator, imported
 * from Excel, and left over from demo data -- so they differ in case,
 * spacing and punctuation. Matching on the raw string fails silently.
 * ------------------------------------------------------------------ */

const PLACEHOLDER_NAMES = new Set([
  '',
  'n/a',
  'na',
  'null',
  '[null]',
  '-',
  '--',
  'nil',
  'none',
  'undefined',
  'all banks',
  'partner bank',
  'bank',
  'unknown',
  'unassigned',
]);

/** Lower-cased, collapsed-whitespace form used for every comparison. */
export function norm(value?: string | null): string {
  return (value || '').toString().trim().replace(/\s+/g, ' ').toLowerCase();
}

/** True when this name carries no real information. */
export function isPlaceholderName(value?: string | null): boolean {
  return PLACEHOLDER_NAMES.has(norm(value));
}

/**
 * Is this the name of a bank that actually exists?
 *
 * WHY THIS MATTERS: `addBranch` used to fill in a missing bank with a
 * hard-coded default -- `bankName: branchData.bankName || 'State Bank of
 * India'`. So any branch created without an explicit bank was stamped with
 * that name, whether or not the administrator had ever added such a bank.
 * The same was true of Recovery Departments.
 *
 * Batch 25 stopped new records being stamped, but the existing ones still
 * carry the name. A branch claiming to belong to a bank that is not in the
 * system is not really parented to anything -- it is leftover default data --
 * so it is treated as unassigned rather than as belonging to a bank nobody
 * created. Otherwise those branches become unusable: they cannot be picked
 * under the real bank, and the bank they name does not exist to pick.
 */
export function isKnownBankName(name: string | undefined, banks: Bank[]): boolean {
  if (isPlaceholderName(name)) return false;
  return (banks || []).some((b) => sameName(b.name, name));
}

export function sameName(a?: string | null, b?: string | null): boolean {
  const x = norm(a);
  const y = norm(b);
  if (!x || !y) return false;
  return x === y;
}

/* ------------------------------------------------------------------ *
 * Filtering: children of a parent
 * ------------------------------------------------------------------ */

/**
 * The zones of one bank.
 *
 * A zone with no bank recorded is shown under every bank rather than being
 * hidden. Existing zones pre-date this field; hiding them would make the
 * live data look empty and an administrator would recreate it as duplicates.
 * Once the zone is edited and given a bank it stops appearing elsewhere.
 */
export function zonesOfBank(zones: Zone[], bankName?: string, bankId?: string): Zone[] {
  const list = zones || [];
  if (!bankName && !bankId) return list;
  return list.filter((z) => {
    if (!z) return false;
    if (bankId && z.bankId && z.bankId === bankId) return true;
    if (bankName && sameName(z.bankName, bankName)) return true;
    // Unparented zone -- visible everywhere until it is given a bank.
    return !z.bankId && isPlaceholderName(z.bankName);
  });
}

/**
 * The branches of one bank and one zone.
 *
 * Passing no zone gives every branch of the bank, which is what the
 * allocation and re-allocation filters want.
 */
export function branchesOfBankZone(
  branches: Branch[],
  bankName?: string,
  zoneName?: string,
  /** Pass the real bank list and orphaned branches become selectable again. */
  banks?: Bank[]
): Branch[] {
  const list = branches || [];
  return list.filter((br) => {
    if (!br) return false;

    if (bankName) {
      const orphaned = banks ? !isKnownBankName(br.bankName, banks) : false;
      const bankOk =
        sameName(br.bankName, bankName) || isPlaceholderName(br.bankName) || orphaned;
      if (!bankOk) return false;
    }

    if (zoneName) {
      const zoneOk =
        sameName(br.zoneName, zoneName) ||
        sameName(br.zone, zoneName) ||
        (isPlaceholderName(br.zoneName) && isPlaceholderName(br.zone));
      if (!zoneOk) return false;
    }

    return true;
  });
}

/** The recovery departments of one bank, optionally narrowed to one zone. */
export function departmentsOfBankZone(
  departments: RecoveryDepartment[],
  bankName?: string,
  zoneName?: string
): RecoveryDepartment[] {
  return (departments || []).filter((d) => {
    if (!d) return false;
    if (bankName && !(sameName(d.bankName, bankName) || isPlaceholderName(d.bankName))) {
      return false;
    }
    if (zoneName && !(sameName(d.zoneName, zoneName) || isPlaceholderName(d.zoneName))) {
      return false;
    }
    return true;
  });
}

/** Resolve a bank by name, so an id is never guessed. */
export function findBankByName(banks: Bank[], name?: string): Bank | undefined {
  if (!name) return undefined;
  return (banks || []).find((b) => sameName(b.name, name));
}

export function findZoneByName(
  zones: Zone[],
  name?: string,
  bankName?: string
): Zone | undefined {
  if (!name) return undefined;
  const candidates = (zones || []).filter((z) => sameName(z.name, name));
  if (candidates.length <= 1) return candidates[0];
  // Two banks may both have a "Pune Zone". The bank decides which one.
  return (
    candidates.find((z) => sameName(z.bankName, bankName)) || candidates[0]
  );
}

export function findBranchByName(
  branches: Branch[],
  name?: string,
  bankName?: string,
  zoneName?: string
): Branch | undefined {
  if (!name) return undefined;
  const candidates = (branches || []).filter((b) => sameName(b.name, name));
  if (candidates.length <= 1) return candidates[0];
  return (
    candidates.find(
      (b) => sameName(b.bankName, bankName) && (!zoneName || sameName(b.zoneName, zoneName))
    ) ||
    candidates.find((b) => sameName(b.bankName, bankName)) ||
    candidates[0]
  );
}

/* ------------------------------------------------------------------ *
 * What each role needs before it may be created
 * ------------------------------------------------------------------ */

export interface RoleRequirement {
  bank: boolean;
  zone: boolean;
  /** 'one' = exactly one branch. 'many' = at least one. false = not required. */
  branch: false | 'one' | 'many';
  agentId: boolean;
  plan: boolean;
  /** Plain words for the screen, so the rule and its explanation cannot drift. */
  note: string;
}

export function requirementsFor(role: UserRole): RoleRequirement {
  switch (role) {
    case 'admin':
    case 'management':
      return {
        bank: false,
        zone: false,
        branch: false,
        agentId: false,
        plan: false,
        note: 'Administrators see every bank, so no branch is assigned. Free of charge.',
      };

    case 'recovery_department':
      return {
        bank: true,
        zone: true,
        branch: false,
        agentId: false,
        plan: false,
        note:
          'Recovery Department needs a bank and a zone. Branch is optional — leaving it blank means the whole zone. VIEW ONLY. Free of charge.',
      };

    case 'branch_manager':
      return {
        bank: true,
        zone: true,
        branch: 'one',
        agentId: false,
        plan: false,
        note:
          'A Branch Manager is tied to exactly ONE branch and cannot switch. View only, except for approving OTS. Free of charge.',
      };

    case 'coordinator':
      return {
        bank: true,
        zone: true,
        branch: 'many',
        agentId: false,
        plan: false,
        note: 'A Coordinator needs a bank, a zone and at least one branch.',
      };

    case 'agent':
    default:
      return {
        bank: true,
        zone: true,
        branch: 'many',
        agentId: true,
        plan: true,
        note:
          'An Agent needs a bank, a zone, at least one branch, an Agent ID and either a Free Demo or a Subscription.',
      };
  }
}

/* ------------------------------------------------------------------ *
 * Validation -- no orphan users
 * ------------------------------------------------------------------ */

export interface HierarchySelection {
  role: UserRole;
  banks: string[];
  zone: string;
  branches: string[];
  agentId?: string;
  plan?: 'demo' | 'subscription' | '';
  demoStartDate?: string;
  demoEndDate?: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  subscriptionAmount?: number | string;
  paymentDate?: string;
  paymentMode?: string;
}

/**
 * Every reason this user cannot be created, in the order they appear on the
 * form. An empty list means Create User may proceed.
 */
export function validateUserHierarchy(
  sel: HierarchySelection,
  zones: Zone[],
  branches: Branch[],
  /**
   * The real bank list. Without it, a branch stamped with the old hard-coded
   * "State Bank of India" default is refused against a bank that was never
   * created -- which is impossible for the administrator to resolve, because
   * the bank it names does not exist to be ticked.
   */
  allBanks: Bank[] = []
): string[] {
  const need = requirementsFor(sel.role);
  const problems: string[] = [];

  const banks = (sel.banks || []).filter((b) => !isPlaceholderName(b));
  const branchList = (sel.branches || []).filter((b) => !isPlaceholderName(b));
  const zone = (sel.zone || '').trim();

  if (need.bank && banks.length === 0) {
    problems.push('Select a Bank.');
  }

  if (need.zone && !zone) {
    problems.push('Select a Zonal Office.');
  }

  if (need.branch === 'one') {
    if (branchList.length === 0) problems.push('Select the Branch this manager runs.');
    if (branchList.length > 1) {
      problems.push('A Branch Manager may hold only ONE branch. Untick the others.');
    }
  }
  if (need.branch === 'many' && branchList.length === 0) {
    problems.push('Tick at least one Branch.');
  }

  // The chosen zone must actually belong to the chosen bank, and each chosen
  // branch to that bank and zone. Without this the dropdowns could be right
  // and the saved record still wrong -- a bank changed after the branch was
  // ticked used to leave the earlier branch in place.
  if (banks.length > 0 && zone) {
    const primary = banks[0];
    const zoneRecord = findZoneByName(zones, zone, primary);
    if (
      zoneRecord &&
      zoneRecord.bankName &&
      !isPlaceholderName(zoneRecord.bankName) &&
      // A bank that does not exist is leftover default data, not a conflict.
      isKnownBankName(zoneRecord.bankName, allBanks) &&
      !banks.some((b) => sameName(b, zoneRecord.bankName))
    ) {
      problems.push(
        `Zone "${zone}" belongs to ${zoneRecord.bankName}, which is not one of the ticked banks.`
      );
    }
  }

  if (banks.length > 0 && branchList.length > 0) {
    branchList.forEach((brName) => {
      const br = findBranchByName(branches, brName, banks[0], zone);
      if (!br) return;
      if (
        br.bankName &&
        !isPlaceholderName(br.bankName) &&
        // Same reasoning: only a REAL competing bank is a conflict worth
        // blocking on. "State Bank of India" stamped by the old default is
        // not a bank this administrator has, so it is treated as unassigned.
        isKnownBankName(br.bankName, allBanks) &&
        !banks.some((b) => sameName(b, br.bankName))
      ) {
        problems.push(
          `Branch "${brName}" belongs to ${br.bankName}, which is not one of the ticked banks.`
        );
      }
    });
  }

  if (need.agentId && !(sel.agentId || '').trim()) {
    problems.push('Enter the Agent ID (for example RA-0048).');
  }

  if (need.plan) {
    if (!sel.plan) {
      problems.push('Choose Free Demo or Subscription.');
    } else if (sel.plan === 'demo') {
      if (!sel.demoStartDate) problems.push('Enter the Demo Start Date.');
      if (!sel.demoEndDate) problems.push('Enter the Demo End Date.');
      if (
        sel.demoStartDate &&
        sel.demoEndDate &&
        sel.demoEndDate < sel.demoStartDate
      ) {
        problems.push('Demo End Date is before the Demo Start Date.');
      }
    } else {
      if (!sel.subscriptionStartDate) problems.push('Enter the Subscription Start Date.');
      if (!sel.subscriptionEndDate) problems.push('Enter the Subscription End Date.');
      if (
        sel.subscriptionStartDate &&
        sel.subscriptionEndDate &&
        sel.subscriptionEndDate < sel.subscriptionStartDate
      ) {
        problems.push('Subscription End Date is before the Subscription Start Date.');
      }
      const amt = Number(sel.subscriptionAmount);
      if (!amt || amt <= 0) problems.push('Enter the Subscription Amount.');
      if (!sel.paymentDate) problems.push('Enter the Payment Date.');
      if (!(sel.paymentMode || '').trim()) problems.push('Select the Payment Mode.');
    }
  }

  return problems;
}

/* ------------------------------------------------------------------ *
 * Subscription status -- shown, never enforced
 * ------------------------------------------------------------------ */

export type PlanStatus = 'none' | 'free' | 'active' | 'expiring' | 'expired';

export function planStatusOf(user: User, today = new Date()): {
  status: PlanStatus;
  label: string;
  endDate?: string;
  daysLeft?: number;
} {
  if (!user) return { status: 'none', label: '—' };
  if (user.role !== 'agent') return { status: 'free', label: 'Free' };
  if (!user.agentPlan) return { status: 'none', label: 'No plan set' };

  const end =
    user.agentPlan === 'demo' ? user.demoEndDate : user.subscriptionEndDate;
  const word = user.agentPlan === 'demo' ? 'Demo' : 'Subscription';
  if (!end) return { status: 'none', label: `${word} — no end date` };

  const endTime = new Date(`${end}T23:59:59`).getTime();
  const nowTime = today.getTime();
  const daysLeft = Math.ceil((endTime - nowTime) / 86400000);

  if (daysLeft < 0) {
    return { status: 'expired', label: `${word} expired`, endDate: end, daysLeft };
  }
  if (daysLeft <= 7) {
    return {
      status: 'expiring',
      label: `${word} ends in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`,
      endDate: end,
      daysLeft,
    };
  }
  return { status: 'active', label: `${word} active`, endDate: end, daysLeft };
}

/* ------------------------------------------------------------------ *
 * Role scope -- what a signed-in user may do
 * ------------------------------------------------------------------ */

/**
 * Roles that may only look.
 *
 * Branch Manager keeps ONE exception: approving (or declining) an OTS offer.
 * That right was given deliberately, because an agent must not approve their
 * own offer and somebody at the branch has to. Everything else -- recording a
 * collection, editing an account, deleting anything -- is refused.
 *
 * WHERE THIS IS ENFORCED: in the application, at `mutateAccounts` -- the one
 * function every account change passes through -- and at the delete paths.
 * That covers every screen, including screens added later that forget to
 * check, which is why it is done there rather than by hiding buttons.
 *
 * It is NOT yet enforced in the Firestore security rules. A determined user
 * with the browser console open could still write. Closing that needs the
 * rules to distinguish an OTS-approval write from any other account write,
 * which is real work and is listed as outstanding rather than claimed here.
 */
const VIEW_ONLY_ROLES: UserRole[] = ['branch_manager', 'recovery_department'];

export function isViewOnly(user?: User | null): boolean {
  if (!user) return true;
  return VIEW_ONLY_ROLES.includes(user.role);
}

export function canApproveOTS(user?: User | null): boolean {
  if (!user) return false;
  return (
    user.role === 'admin' ||
    user.role === 'management' ||
    user.role === 'recovery_department' ||
    user.role === 'branch_manager'
  );
}

/** Plain-words refusal for the screen, or null when the action is allowed. */
export function writeRefusal(user?: User | null, action = 'change anything'): string | null {
  if (!user) return 'You are not signed in.';
  if (!isViewOnly(user)) return null;
  const where =
    user.role === 'branch_manager'
      ? `${user.branch || 'your branch'}`
      : `${user.zone || user.zonalOffice || 'your zone'}`;
  return `Your role (${user.role.replace(/_/g, ' ')}) is view only for ${where}. You cannot ${action}.`;
}

/**
 * The branches a signed-in user may see.
 *
 * An empty result means "no restriction" for admin and management; every
 * other role gets an explicit list, and an empty list for them means they
 * were created without a branch and should see nothing.
 */
export function scopeBranchesOf(user?: User | null): string[] {
  if (!user) return [];
  if (user.role === 'admin' || user.role === 'management') return [];
  const list = (user.branches && user.branches.length ? user.branches : [user.branch])
    .filter((b): b is string => !!b && !isPlaceholderName(b));
  return Array.from(new Set(list));
}

export function scopeBanksOf(user?: User | null): string[] {
  if (!user) return [];
  if (user.role === 'admin' || user.role === 'management') return [];
  const list = (user.banks && user.banks.length ? user.banks : [user.bank])
    .filter((b): b is string => !!b && !isPlaceholderName(b));
  return Array.from(new Set(list));
}

/* ------------------------------------------------------------------ *
 * WHO MAY MESSAGE WHOM
 *
 * Administrators and management may message any agent. A branch manager
 * may message the agents in their own branches, and a recovery department
 * user the agents in their own bank -- narrowed to their zone when both
 * the user and the agent have one recorded.
 *
 * An agent may not message anybody. This is an instruction channel from
 * authority downward, not a chat: a private line between agents is a
 * different feature with different rules, and inventing it quietly here
 * would be the wrong way to arrive at it.
 * ------------------------------------------------------------------ */

export function canSendMessages(user?: User | null): boolean {
  if (!user) return false;
  return (
    user.role === 'admin' ||
    user.role === 'management' ||
    user.role === 'branch_manager' ||
    user.role === 'recovery_department'
  );
}

/** Only an administrator removes anything from an inbox. */
export function canDeleteMessages(user?: User | null): boolean {
  return user?.role === 'admin';
}

/** The agents `sender` is allowed to write to, out of everybody. */
export function messageRecipientsFor(sender: User | null | undefined, all: User[]): User[] {
  if (!sender || !canSendMessages(sender)) return [];

  const agents = (all || []).filter(
    (u) => u.role === 'agent' && u.active !== false && !(u as any).isDeleted
  );

  if (sender.role === 'admin' || sender.role === 'management') return agents;

  if (sender.role === 'branch_manager') {
    const mine = scopeBranchesOf(sender).map((b) => norm(b));
    if (mine.length === 0) return [];
    return agents.filter((a) => {
      const theirs = (a.branches && a.branches.length ? a.branches : [a.branch])
        .filter(Boolean)
        .map((b) => norm(b as string));
      return theirs.some((b) => mine.includes(b));
    });
  }

  // Recovery department: their bank, and their zone when both name one.
  const myBanks = scopeBanksOf(sender).map((b) => norm(b));
  return agents.filter((a) => {
    const theirBanks = (a.banks && a.banks.length ? a.banks : [a.bank])
      .filter(Boolean)
      .map((b) => norm(b as string));
    // An agent with no bank recorded is unclassified, not out of scope --
    // the same rule the account screens use.
    const bankOk =
      myBanks.length === 0 || theirBanks.length === 0 || theirBanks.some((b) => myBanks.includes(b));
    if (!bankOk) return false;
    if (!sender.zone || !a.zone) return true;
    return norm(sender.zone) === norm(a.zone);
  });
}
